import {createParamSelector, getUseQueryResultData} from "../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";
import {RawStatistics} from "../../model/statistics/raw-statistics";
import {
  DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS,
  StatisticsBundleBuilder,
  StatisticsBundleBuilderOptions
} from "../../model/statistics/statistics-bundle-builder";
import {selectPipelineFlowTransportsAndTransform} from "../entity/pipeline-flow-transport/selectors";
import {TransportAssoc} from "../../model/transport-assoc";

const getProcessRegionParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processRegion);
const getProcessOperatorCompanyParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processOperatorCompany);
const getProcessTruckCompanyParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processTruckCompany);
const getProcessVendorCompanyParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processVendorCompany);
const getProcessPipelineParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processPipeline);
const getProcessContainerParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processContainer);
const getProcessWellParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processWell);
const getProcessSiteParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processSite);
const getProcessRouteParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processRoute);
const getProcessRouteGroupParam = createParamSelector<StatisticsBundleBuilderOptions | undefined, boolean | undefined>(params => params?.processRouteGroup);

const getIdParam = createParamSelector<string, string>(params => params);

export const statisticsSelector = () => createSelector(
  [
    getUseQueryResultData<RawStatistics>,
    getProcessRegionParam,
    getProcessOperatorCompanyParam,
    getProcessTruckCompanyParam,
    getProcessVendorCompanyParam,
    getProcessPipelineParam,
    getProcessContainerParam,
    getProcessWellParam,
    getProcessSiteParam,
    getProcessRouteParam,
    getProcessRouteGroupParam,
  ],
  (
    data,
    processRegion,
    processOperatorCompany,
    processTruckCompany,
    processVendorCompany,
    processPipeline,
    processContainer,
    processWell,
    processSite,
    processRoute,
    processRouteGroup,
  ) => {
    const options = {
      ...DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS,
      processRegion: processRegion ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processRegion,
      processOperatorCompany: processOperatorCompany ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processOperatorCompany,
      processTruckCompany: processTruckCompany ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processTruckCompany,
      processVendorCompany: processVendorCompany ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processVendorCompany,
      processPipeline: processPipeline ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processPipeline,
      processContainer: processContainer ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processContainer,
      processWell: processWell ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processWell,
      processSite: processSite ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processSite,
      processRoute: processRoute ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processRoute,
      processRouteGroup: processRouteGroup ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processRouteGroup,
    }
    return StatisticsBundleBuilder.build(data, options)
  }
);

export const regionStatisticsSelector = () => createSelector(
  [getUseQueryResultData<RawStatistics>, getIdParam],
  (data, id) => {
    const bundle = StatisticsBundleBuilder.build(data, {processRegion: true})
    return bundle?.getRegionStats(id)
  }
);

export const pipelineStatisticsSelector = () => createSelector(
  [getUseQueryResultData<RawStatistics>, getIdParam],
  (data, id) => {
    const bundle = StatisticsBundleBuilder.build(data, {processPipeline: true})
    return bundle?.getPipelineStats(id)
  }
);

export const siteStatisticsSelector = () => createSelector(
  [getUseQueryResultData<RawStatistics>, getIdParam],
  (data, id) => {
    const bundle = StatisticsBundleBuilder.build(data, {processSite: true})
    return bundle ? {
      all: bundle.getSiteStats(id),
      pickup: bundle.getPickupSiteStats(id),
      delivery: bundle.getDeliverySiteStats(id),
    } : undefined
  }
);

export const flowRawStatisticsSelector = () => createSelector(
  [getUseQueryResultData<RawStatistics>],
  (data) => {
    const flows = data?.flowTransports ?? []
    return selectPipelineFlowTransportsAndTransform(flows, {})
  }
);

export const ttaStatisticsSelector = () => createSelector(
  [
    getUseQueryResultData<TransportAssoc[]>,
    getProcessRegionParam,
    getProcessOperatorCompanyParam,
    getProcessTruckCompanyParam,
    getProcessVendorCompanyParam,
    getProcessPipelineParam,
    getProcessContainerParam,
    getProcessWellParam,
    getProcessSiteParam,
    getProcessRouteParam,
    getProcessRouteGroupParam,
  ],
  (
    data,
    processRegion,
    processOperatorCompany,
    processTruckCompany,
    processVendorCompany,
    processPipeline,
    processContainer,
    processWell,
    processSite,
    processRoute,
    processRouteGroup,
  ) => {
    const options = {
      ...DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS,
      processRegion: processRegion ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processRegion,
      processOperatorCompany: processOperatorCompany ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processOperatorCompany,
      processTruckCompany: processTruckCompany ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processTruckCompany,
      processVendorCompany: processVendorCompany ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processVendorCompany,
      processPipeline: processPipeline ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processPipeline,
      processContainer: processContainer ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processContainer,
      processWell: processWell ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processWell,
      processSite: processSite ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processSite,
      processRoute: processRoute ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processRoute,
      processRouteGroup: processRouteGroup ?? DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.processRouteGroup,
    }
    return StatisticsBundleBuilder.build({ttaList: data}, options)
  }
);
